import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

interface RouteMetadata {
  title: string;
  description: string;
  keywords: string[];
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
}

const routeMeta: Record<string, RouteMetadata> = {
  '/': {
    title: 'Technical Innovation Architect',
    description: 'Principal Member of Technical Staff with expertise in edge computing, content delivery networks, and distributed systems optimization.',
    keywords: ['Software Engineer', 'Edge Computing', 'CDN', 'Distributed Systems'],
    type: 'website'
  },
  '/journey': {
    title: 'Professional Journey',
    description: 'Track record of technical leadership and innovation across edge computing, platform architecture, and distributed systems.',
    keywords: ['Technical Leadership', 'Software Architecture', 'Edge Computing'],
    type: 'website'
  },
  '/research': {
    title: 'Research & Publications',
    description: 'Four peer-reviewed publications (2012–2026) across IEEE and Springer, plus patents and technical contributions in scholarly networking and visualization frameworks.',
    keywords: ['Technical Publications', 'Patents', 'Research'],
    type: 'article',
    publishedTime: '2024-03-20',
    modifiedTime: '2024-03-20'
  },
  '/community': {
    title: 'Community Impact',
    description: 'Contributing to community development through technical leadership, mentorship, and organizational innovation.',
    keywords: ['Community Leadership', 'Mentorship', 'Social Impact'],
    type: 'website'
  },
  '/contact': {
    title: 'Contact',
    description: 'Connect for technical collaboration, research opportunities, or professional inquiries.',
    keywords: ['Contact', 'Professional Network', 'Collaboration'],
    type: 'website'
  }
};

interface SectionProps {
  children: React.ReactNode;
  className?: string;
}

const Section: React.FC<SectionProps> = ({ children, className = '' }) => (
  <section className={`relative ${className}`}>
    {children}
  </section>
);

interface PageLayoutProps {
  children: React.ReactNode;
}

const PageLayout: React.FC<PageLayoutProps> = ({ children }) => {
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const childrenArray = React.Children.toArray(children);
  
  const siteTitle = 'Chintan Tank';
  const siteUrl = 'https://chintantank.info';
  
  const meta = routeMeta[location.pathname] || routeMeta['/'];
  const fullTitle = `${meta.title} | ${siteTitle}`;
  const canonical = `${siteUrl}${location.pathname}`;

  // // Add this useEffect to reset scroll position on route changes
  // useEffect(() => {
  //   window.scrollTo(0, 0);
  // }, [location.pathname]);

  useEffect(() => {
    // Reset scroll position on route changes
    if (mainRef.current) {
      mainRef.current.scrollTop = 0;
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return (
    <main ref={mainRef} className="min-h-screen relative">
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords.join(', ')} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={canonical} />
        
        {/* Open Graph Tags */}
        <meta property="og:site_name" content={siteTitle} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:type" content={meta.type} />
        <meta property="og:url" content={canonical} />
        
        {/* Article Specific Tags */}
        {meta.type === 'article' && meta.publishedTime && (
          <meta property="article:published_time" content={meta.publishedTime} />
        )}
        {meta.type === 'article' && meta.modifiedTime && (
          <meta property="article:modified_time" content={meta.modifiedTime} />
        )}
        
        {/* Twitter Card Tags - Basic card instead of large image */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={meta.description} />
      </Helmet>

      {childrenArray.map((child, index) => {
        // Let each section control its own background and dividers
        return (
          <React.Fragment key={index}>
            {child}
          </React.Fragment>
        );
      })}
    </main>
  );
};

export default PageLayout;